import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getRooms, saveRoom } from "../../components/Api/GmsRoom";
import { SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const defaultForm = {
  Room_Id: null,
  Room_Code: "",
  Room_Name_English: "",
  Room_Name_Arabic: "",
  Status: true,
};

const emptyFilter = {
  Room_Name_English: "",
  Status: null,
};

export default function RoomPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsRoom');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsRoom');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsRoom');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsRoom');
  const isAdmin = isUserAdmin();

  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState(() => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.Status : true;
    return { ...defaultForm, Status: defaultStatus };
  });
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  useEffect(() => {
    if (hasPermission) {
      loadRooms();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadRooms();
    }
  }, [hasAppliedFilter]);

  const loadRooms = async (filterData = filter) => {
    setLoading(true);
    try {
      const data = await getRooms(filterData);
      setRooms(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load rooms: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.Room_Name_English) {
      Swal.fire("Error", "Please fill in Room Name (English)", "error");
      return;
    }

    setSaveLoading(true);
    try {
      // Prepare save data - remove ID field for inserts
      const saveData = { ...form };
      
      // Remove Room_Id if it's null, 0, or undefined (insert operation)
      if (!saveData.Room_Id || saveData.Room_Id === 0) {
        delete saveData.Room_Id;
      }
      
      const result = await saveRoom(saveData);
      
      if (result) {
        Swal.fire("Success", "Room saved successfully!", "success");
        handleClear();
        if (hasAppliedFilter) {
          loadRooms();
        }
      }
    } catch (error) {
      let errorMessage = "Failed to save room";
      
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          const match = error.response.data.match(/System\.InvalidOperationException:\s*(.+?)(?:\s+at\s+|$)/);
          errorMessage = match ? match[1].trim() : error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (room) => {
    setEditLoadingRow(room.room_Code);
    try {
      setForm({
        Room_Id: room.room_Id || 0,
        Room_Code: room.room_Code || "",
        Room_Name_English: room.room_Name_English || "",
        Room_Name_Arabic: room.room_Name_Arabic || "",
        Status: room.status,
      });
      setIsEditing(true);
      setActiveTab("1");
    } catch (error) {
      Swal.fire("Error", "Failed to load room data", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleClear = () => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.Status : true;
    setForm({ ...defaultForm, Status: defaultStatus });
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
    setPage(1);
    loadRooms(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(false);
    setRooms([]);
    setTotalCount(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getFilteredRooms = () => {
    if (!hasAppliedFilter) return rooms;
    
    return rooms.filter(room => {
      const nameMatch = !filter.Room_Name_English || 
        room.room_Name_English.toLowerCase().includes(filter.Room_Name_English.toLowerCase());
      const statusMatch = filter.Status === null || room.status === filter.Status;
      return nameMatch && statusMatch;
    });
  };

  const getPaginatedRooms = () => {
    const filteredRooms = getFilteredRooms();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredRooms.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(totalCount / pageSize);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      if (tab === "2") {
        loadRooms();
      }
    }
  };

  const getRoomNameOptions = () => {
    return rooms.map(room => ({
      value: room.room_Name_English,
      label: room.room_Name_English
    }));
  };

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Room Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => toggle("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Room Form</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => toggle("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Room List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="Room_Name_English">Room Name (English) <span className="text-danger">*</span></Label>
                              <Input
                                type="text"
                                id="Room_Name_English"
                                value={form.Room_Name_English}
                                onChange={(e) => handleFormChange("Room_Name_English", e.target.value)}
                                placeholder="Enter room name in English"
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="Room_Name_Arabic">Room Name (Arabic)</Label>
                              <Input
                                type="text"
                                id="Room_Name_Arabic"
                                value={form.Room_Name_Arabic}
                                onChange={(e) => handleFormChange("Room_Name_Arabic", e.target.value)}
                                placeholder="Enter room name in Arabic"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        {hasDeletePermission && (
                          <Row>
                            <Col md={4}>
                              <FormGroup>
                                <div className="form-check form-switch">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="room_Status"
                                    name="Status"
                                    checked={form.Status}
                                    onChange={(e) => handleFormChange("Status", e.target.checked)}
                                  />
                                  <label className="form-check-label" htmlFor="room_Status">
                                    {form.Status ? "Active" : "Inactive"}
                                  </label>
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        )}
                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Room" : "Save Room"}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </TabPane>
                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Rooms</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={4}>
                                <Label for="filterRoomName">Room Name</Label>
                                <Select
                                  id="filterRoomName"
                                  value={getRoomNameOptions().find(option => option.value === filter.Room_Name_English)}
                                  onChange={(selectedOption) => 
                                   handleFilterChange("Room_Name_English", selectedOption ? selectedOption.value : "")
                                  }
                                  options={getRoomNameOptions()}
                                  isClearable
                                  isSearchable
                                  placeholder="Search room name..."
                                  className="react-select"
                                  classNamePrefix="react-select"
                               />
                             </Col>
                             <Col md={4}>
                               <Label for="filterStatus">Status</Label>
                               <Select
                                 id="filterStatus"
                                 value={statusOptions.find(option => option.value === filter.Status)}
                                 onChange={(selectedOption) =>
                                   handleFilterChange("Status", selectedOption ? selectedOption.value : null)
                                 }
                                 options={statusOptions}
                                 isClearable
                                 placeholder="Select Status"
                                 className="react-select"
                                 classNamePrefix="react-select"
                               />
                             </Col>
                            <Col md={4} className="d-flex align-items-end">
                              <div className="d-flex gap-2">
                                <Button
                                  color="primary"
                                  onClick={handleApplyFilter}
                                  className="waves-effect waves-light"
                                >
                                  Apply Filter
                                </Button>
                                <Button
                                  color="secondary"
                                  onClick={handleClearFilter}
                                  className="waves-effect waves-light"
                                >
                                  Clear Filter
                                </Button>
                              </div>
                            </Col>
                          </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Room List
                            </h5>
                            {hasEditOrHigher && (
                              <Button
                                color="primary"
                                onClick={() => toggle("1")}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Room
                              </Button>
                            )}
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading rooms...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                   <thead className="table-light">
                                     <tr>
                                       <th width="10%">Serial Number</th>
                                       <th width="25%">Room Name (En)</th>
                                       <th width="25%">Room Name (Ar)</th>
                                       <th width="15%">Status</th>
                                       <th width="10%">Actions</th>
                                     </tr>
                                   </thead>
                                  <tbody>
                                    {getPaginatedRooms().length > 0 ? (
                                      getPaginatedRooms().map((room, index) => (
                                        <tr key={room.room_Id}>
                                          <td>{(page - 1) * pageSize + index + 1}</td>
                                          <td>{room.room_Name_English}</td>
                                          <td>{room.room_Name_Arabic || "N/A"}</td>
                                          <td>
                                            <span className={`badge ${room.status ? 'bg-success' : 'bg-danger'}`}>
                                              {room.status ? "Active" : "Inactive"}
                                            </span>
                                          </td>
                                          <td>
                                            {hasEditOrHigher && (
                                              <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => handleEdit(room)}
                                                disabled={editLoadingRow === room.room_Code}
                                                className="waves-effect waves-light"
                                              >
                                                {editLoadingRow === room.room_Code ? (
                                                  <ClipLoader size={12} color="#ffffff" />
                                                ) : (
                                                  <SquarePen size={12} />
                                                )}
                                              </Button>
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="5" className="text-center">
                                          {hasAppliedFilter ? "No rooms found matching the filter criteria." : "No rooms available. Please add some rooms first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {getTotalPages() > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {getTotalPages()}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === getTotalPages()}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
